/**
 * Live weather for the resort, from Open-Meteo (D67).
 *
 * **A fetch from the browser, not a service.** D12 keeps the schedule, the map
 * and the credits as files rather than endpoints; this is the same instinct one
 * step further — Open-Meteo needs no API key, so there is nothing to hide and
 * therefore no reason for a Lambda to stand in the middle. A proxy would buy
 * one shared hourly fetch instead of one per guest, and cost a route, a deploy,
 * and a new way for the weather to be down while the site is up. 166 guests
 * refreshing hourly is a few thousand calls across the week against a free
 * allowance of 10,000 a day.
 *
 * **Attribution is a licence condition, not a courtesy.** Open-Meteo's data is
 * CC BY 4.0, so `ATTRIBUTION` below has to stay visible wherever any of this is
 * drawn — the same standing as the ODbL credit on the resort plan. Both pages
 * that render weather print it in their footer line.
 *
 * **Weather is a nicety and must never become a failure.** There is no error
 * state anywhere in this file's consumers: no data means no card, and the page
 * reads exactly as it did before this existed. That is deliberate. A guest
 * standing in the lobby wanting to know when the haldi starts must never be
 * shown a broken weather box, and principles 3 and 4 already say a spinner is
 * not allowed where a cached copy exists.
 */

import { useEffect, useState } from 'react';
import { ORIGIN } from '../data/resort-plan';
import { dayKey, now, parseKhaoLak, TZ } from './time';

/** CC BY 4.0. See the note above — this is a condition of use. */
export const ATTRIBUTION = 'Weather from Open-Meteo';
export const ATTRIBUTION_URL = 'https://open-meteo.com/';

/**
 * How long a reading is good for. The user asked for hourly and the API's own
 * `current` block updates every 15 minutes, so this is the slower of the two
 * and the one that matters: a guest who leaves the app open all afternoon gets
 * a new reading each hour rather than on every render.
 */
export const REFRESH_MS = 60 * 60 * 1000;

/**
 * Open-Meteo's forecast horizon, and the reason the Schedule's strips appear
 * one day at a time.
 *
 * Sixteen days is the maximum the free endpoint will return, which on 14 Oct
 * 2026 reaches 29 Oct — four days short of the welcome dinner. So the hourly
 * strips are *empty until about 21 October* and then fill in from the top of
 * the week down as each day comes inside the window. `WeatherHours` renders
 * nothing at all for a day it has no hours for, which is why that reads as a
 * schedule without weather rather than as a row of empty boxes.
 */
export const FORECAST_DAYS = 16;

export interface Reading {
  /** Whole degrees Celsius. Nothing downstream wants the decimal. */
  temp: number;
  /**
   * Open-Meteo's `apparent_temperature`, and the single most useful number
   * here: 31.5° measured against 39.5° felt, on the first reading taken while
   * building this. Khao Lak in November is humid, and a guest deciding what to
   * wear is served by the second figure, not the first.
   */
  feelsLike: number;
  humidity: number;
  code: number;
  isDay: boolean;
  windKph: number;
  /** When the reading was taken, in ms. */
  at: number;
}

export interface Hour {
  at: number;
  temp: number;
  /** Per cent, 0–100. */
  rainChance: number;
  code: number;
  /**
   * Daylight at this hour, asked of the API rather than guessed from the
   * clock. Without it every cell defaults to daytime and a clear evening draws
   * a **sun at nine at night** — which is exactly what the first build did, and
   * what it looked like was a broken icon rather than a missing field. Khao Lak
   * sunset moves through November and the reception runs past midnight, so a
   * hardcoded 06:00–18:00 would be wrong at both ends of the week.
   */
  isDay: boolean;
}

export interface WeatherDoc {
  current: Reading | null;
  hours: Hour[];
}

/**
 * The WMO code table, and why it covers snow at 8°N.
 *
 * Open-Meteo answers in WMO 4677 codes, and the half of that standard this
 * coast will ever see is clear, cloud, drizzle, rain, showers and thunder. The
 * table is total anyway — freezing rain and snow grains map onto the nearest
 * shape we have a glyph for — because the alternative is a `default` branch
 * that renders an unlabelled gap, and an unrecognised code is exactly the case
 * nobody would notice in testing. A wrong-but-sane label for an impossible
 * code is cheaper than a blank one.
 *
 * Labels are the short form on purpose: they sit in a 60px column on the
 * hourly strip, so "Thunderstorm with slight hail" is not available to us.
 */
const CODES: Record<number, string> = {
  0: 'Clear', 1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Freezing fog',
  51: 'Light drizzle', 53: 'Drizzle', 55: 'Heavy drizzle',
  56: 'Freezing drizzle', 57: 'Freezing drizzle',
  61: 'Light rain', 63: 'Rain', 65: 'Heavy rain',
  66: 'Freezing rain', 67: 'Freezing rain',
  71: 'Light snow', 73: 'Snow', 75: 'Heavy snow', 77: 'Snow grains',
  80: 'Light showers', 81: 'Showers', 82: 'Heavy showers',
  85: 'Snow showers', 86: 'Snow showers',
  95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Thunderstorm',
};

export type Glyph = 'sun' | 'moon' | 'partly' | 'cloud' | 'drizzle' | 'rain' | 'storm' | 'fog';

/**
 * Which glyph a code wants. `isDay` only changes the clear-sky answer — a
 * rained-on night still wants the rain cloud, and swapping in a moon there
 * would say something about the sky the code did not.
 */
export function glyphFor(code: number, isDay = true): Glyph {
  if (code === 0 || code === 1) return isDay ? 'sun' : 'moon';
  if (code === 2) return 'partly';
  if (code === 3) return 'cloud';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if (code >= 95) return 'storm';
  if (code >= 61 && code <= 86) return 'rain';
  return 'cloud';
}

/** The short label for a code. Unknown codes get a word rather than a hole. */
export const labelFor = (code: number): string => CODES[code] ?? 'Cloudy';

/**
 * Is this hour worth warning anybody about?
 *
 * The threshold exists so the strip stays readable. Open-Meteo returns a
 * probability for every hour, and printing "0%" and "3%" down a dry week is
 * eleven rows of noise around the one hour that matters. Twenty-five per cent
 * is the point at which a guest might reasonably carry something.
 */
export const RAIN_THRESHOLD = 25;
export const rainWorthSaying = (hour: Hour): boolean => hour.rainChance >= RAIN_THRESHOLD;

const num = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Turn Open-Meteo's answer into our shape, dropping anything that does not
 * parse rather than trusting the payload wholesale.
 *
 * **The times come back without an offset** — `"2026-11-06T14:00"` — because we
 * ask for `timezone=Asia/Bangkok`, which is what makes them line up with
 * `dayKey` and with the schedule's own wall times. Handing those to
 * `Date.parse` would read them as the *device's* local time, which is the exact
 * mistake `lib/time.ts` was written to prevent: a guest still on New York time
 * would see the 14:00 forecast against the 14:00 holi hour and be eleven hours
 * out. So every timestamp here goes through `parseKhaoLak`, which appends the
 * offset.
 *
 * The three hourly arrays are parallel by contract, and indexed defensively
 * anyway: a short array is a plausible way for this to change under us, and the
 * cost of being wrong is a `NaN` temperature on screen.
 */
export function parseForecast(raw: unknown): WeatherDoc {
  const doc = (raw ?? {}) as Record<string, any>;
  const c = doc.current as Record<string, unknown> | undefined;
  const at = typeof c?.time === 'string' ? parseKhaoLak(c.time as string) : null;
  const temp = num(c?.temperature_2m);

  const current: Reading | null = c && at !== null && temp !== null
    ? {
      temp: Math.round(temp),
      // Falls back to the real temperature rather than to zero: a missing
      // apparent temperature must not read as a cold snap.
      feelsLike: Math.round(num(c.apparent_temperature) ?? temp),
      humidity: Math.round(num(c.relative_humidity_2m) ?? 0),
      code: Math.round(num(c.weather_code) ?? 3),
      // Absent reads as day. Night is the special case and the one that swaps a
      // sun for a moon, so an unknown value should not invent one.
      isDay: num(c.is_day) !== 0,
      windKph: Math.round(num(c.wind_speed_10m) ?? 0),
      at,
    }
    : null;

  const h = doc.hourly as Record<string, unknown[]> | undefined;
  const times = Array.isArray(h?.time) ? h!.time : [];
  const hours: Hour[] = [];
  for (let i = 0; i < times.length; i += 1) {
    const t = typeof times[i] === 'string' ? parseKhaoLak(times[i] as string) : null;
    const temperature = num(h?.temperature_2m?.[i]);
    if (t === null || temperature === null) continue;
    hours.push({
      at: t,
      temp: Math.round(temperature),
      rainChance: Math.round(num(h?.precipitation_probability?.[i]) ?? 0),
      code: Math.round(num(h?.weather_code?.[i]) ?? 3),
      // Absent reads as day, matching the current reading above: night is the
      // case that swaps the glyph, so an unknown value must not invent one.
      isDay: num(h?.is_day?.[i]) !== 0,
    });
  }

  return { current, hours };
}

export const FORECAST_URL = `https://api.open-meteo.com/v1/forecast?latitude=${ORIGIN.lat}`
  + `&longitude=${ORIGIN.lon}`
  + '&current=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,is_day,wind_speed_10m'
  + '&hourly=temperature_2m,precipitation_probability,weather_code,is_day'
  + `&timezone=${encodeURIComponent(TZ)}&forecast_days=${FORECAST_DAYS}`;

/**
 * **Versioned, unlike the `mv.content.*` keys.** Those cache a payload this app
 * does not own the shape of and reads back verbatim; this one caches the
 * *parsed* document, so a change to `parseForecast` would otherwise meet an old
 * object shaped the way the previous parser left it. Bumping the suffix retires
 * every stale copy at once, and the cost of a miss is one fetch.
 */
const CACHE_KEY = 'mv.weather.v1';

function readCache(): { data: WeatherDoc; fetchedAt: number } | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data: WeatherDoc; fetchedAt: number };
    // An hours array is the one field everything downstream iterates.
    if (!parsed?.data || !Array.isArray(parsed.data.hours)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(data: WeatherDoc): number {
  const fetchedAt = Date.now();
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, fetchedAt }));
  } catch {
    /* A full quota costs us the cache, not the reading already on screen. */
  }
  return fetchedAt;
}

const DEMO_KEY = 'mv.wx.demo';

/**
 * Is this session asking for the fabricated forecast? Dev builds only.
 *
 * **Read once at module load, not when the hook runs**, and that is the whole
 * of why this works. `/?wx=demo` is not where anybody ends up: the app
 * redirects a signed-in guest from `/` to `/now` with `<Navigate replace>`,
 * which rewrites the address and takes the query string with it — so by the
 * time `useWeather`'s effect fires, the flag it is looking for has been gone
 * for several renders. Reading at import happens before React has rendered
 * anything, because `App.tsx` imports every route statically and nothing here
 * is lazy.
 *
 * This is exactly the shape `lib/time.ts` uses to latch `?at=`, and it is the
 * same lesson: a URL flag has to be banked before the router is allowed to
 * touch the URL. Session storage then carries it across the reload that
 * pinning the clock performs, so `?wx=demo` and the clock widget compose
 * instead of cancelling each other.
 *
 * `?wx=off` clears it. A flag with no off switch is one you end up clearing by
 * hand in devtools.
 */
function readDemoFlag(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const asked = new URLSearchParams(window.location.search).get('wx');
    if (asked === 'demo') sessionStorage.setItem(DEMO_KEY, '1');
    if (asked === 'off') sessionStorage.removeItem(DEMO_KEY);
    return sessionStorage.getItem(DEMO_KEY) === '1';
  } catch {
    // Private browsing can refuse storage outright. A dev tool is not worth
    // taking the weather down over.
    return false;
  }
}

/**
 * **Gated here, not only at the call site.** `readDemoFlag` used to be called
 * unconditionally and only its *result* was read inside an
 * `import.meta.env.DEV` branch — so the dead branch was eliminated, the demo
 * module really was dropped from the bundle, and this function still shipped
 * and still ran on every real page load, parsing the query string and touching
 * session storage for a flag that could never take effect. Putting the literal
 * first lets Rollup drop the call, the function and the key together.
 */
const demoOn = import.meta.env.DEV && readDemoFlag();

/** Through the end of the farewell brunch, so the demo covers the whole week
 *  however the clock is set. */
const WEEK_END = Date.parse('2026-11-09T00:00:00+07:00');

/**
 * The resort's weather, refreshed hourly.
 *
 * Stale-then-fresh like `useContent`: a cached copy goes on screen at once and
 * the network replaces it underneath. The difference is that this one *expires*
 * — content is re-fetched on every mount because it is small and might have
 * been edited, whereas a reading an hour old is simply wrong and one four
 * minutes old is not worth a round trip.
 *
 * **Two triggers, because neither is sufficient alone.** The interval covers a
 * laptop left open on the schedule; `visibilitychange` covers a phone, where
 * background timers are throttled or stopped outright and the real pattern is a
 * guest pulling the app out of their pocket an hour later. Both call the same
 * guarded function, which reads the clock off the cache and returns without a
 * fetch if the copy in hand is still good — so the two cannot race into a
 * double fetch.
 */
export function useWeather(): WeatherDoc | null {
  /**
   * **The document, and nothing beside it.** This returned a `fetchedAt` and a
   * `stale` flag — copied from `useContent`, where both feed the offline pill
   * — and nothing ever read either. Staleness does matter here, but it is a
   * property of the *reading*, which already carries its own timestamp, so
   * `readingIsCurrent` answers it from the data rather than from hook state
   * that has to be kept honest separately.
   */
  const [state, setState] = useState<WeatherDoc | null>(() => readCache()?.data ?? null);

  useEffect(() => {
    let live = true;
    let fetching = false;

    const refresh = async () => {
      /**
       * The fabricated forecast, and the two reasons it is shaped like this.
       *
       * `import.meta.env.DEV` is a literal `false` in a production build, so
       * Rollup drops this branch and the dynamic import with it — the module
       * never reaches the bundle. A static import at the top of the file would
       * ship it.
       *
       * It is deliberately **not written to the cache**: invented hours
       * persisted into `localStorage` would outlive the session that asked for
       * them and show up in a real one, which is precisely the sort of thing
       * that gets believed.
       */
      if (import.meta.env.DEV && demoOn) {
        const { demoForecast } = await import('./weather-demo');
        if (live) setState(demoForecast(now(), WEEK_END));
        return;
      }
      const cached = readCache();
      if (cached && Date.now() - cached.fetchedAt < REFRESH_MS) return;
      if (fetching) return;
      fetching = true;
      try {
        const res = await fetch(FORECAST_URL, { cache: 'no-store' });
        if (!res.ok) throw new Error(`weather: ${res.status}`);
        const parsed = parseForecast(await res.json());
        // A payload we could not make a single hour or reading out of is not
        // worth caching over a good copy from an hour ago.
        if (!parsed.current && parsed.hours.length === 0) return;
        writeCache(parsed);
        if (live) setState(parsed);
      } catch {
        /* Offline is a state, not an error (principle 4). Whatever is on screen
           stays, and `readingIsCurrent` is what stops a cached measurement
           being presented as the weather right now. There is deliberately
           nothing to show a guest here: see the file header. */
      } finally {
        fetching = false;
      }
    };

    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return state;
}

/**
 * How old a *measurement* may be before it stops being worth showing.
 *
 * The hourly forecast ages gracefully — a forecast for Friday fetched on
 * Thursday is still a forecast for Friday — but `current` is a point-in-time
 * reading, and a day-old "31°, sunny" presented as the weather right now is
 * simply false. A guest offline since yesterday is the realistic way that
 * happens, since the cache is served before the network is consulted.
 *
 * Three hours rather than one: the feed itself is hourly, so a stricter bound
 * would blank the row during normal use for no gain.
 */
export const MAX_READING_AGE_MS = 3 * 60 * 60 * 1000;

/** Is this reading recent enough to present as the current weather? */
export const readingIsCurrent = (reading: Reading, at: number): boolean =>
  at - reading.at < MAX_READING_AGE_MS;

/**
 * The first hour inside `withinMs` that is worth mentioning, or `null`.
 *
 * This is the whole of the rain forecast on Now, and one hour is the right
 * amount: the question a guest has walking out of their room is "do I need
 * anything", which is answered by a time or by silence. A list of
 * probabilities is the Schedule's job.
 *
 * Starts from the *next* hour rather than the current one. An hour already
 * three quarters elapsed is not a forecast, and saying "rain around 14:00" at
 * 14:45 reads as broken rather than as imminent.
 */
export function nextRain(hours: Hour[], at: number, withinMs = 6 * 60 * 60 * 1000): Hour | null {
  return hours.find((h) => h.at > at && h.at <= at + withinMs && rainWorthSaying(h)) ?? null;
}

/**
 * The next `count` hours from `at`, for the pill's panel on Now.
 *
 * Starts at the hour in progress rather than the next one, which is the
 * opposite of `nextRain`'s rule and deliberately so: that function answers
 * "when should I expect rain", where an hour three quarters gone is not a
 * useful answer, while this one is a list a guest reads top to bottom and
 * would look wrong starting an hour ahead of the clock.
 *
 * Capped rather than unbounded because the feed carries sixteen days of hours
 * and the panel is a scroll box on a phone, not an archive.
 */
export function hoursFrom(hours: Hour[], at: number, count = 12): Hour[] {
  const HOUR = 60 * 60 * 1000;
  const from = Math.floor(at / HOUR) * HOUR;
  return hours.filter((h) => h.at >= from).slice(0, count);
}

/**
 * The hours to print under one day of the schedule.
 *
 * Scoped to the day's own programme rather than to midnight-to-midnight, which
 * is the difference between a strip a guest reads and twenty-four columns of
 * mostly sleep. An hour either side of the events, because "what is it doing
 * just before the baraat" is the question being asked.
 *
 * **Clamped to the day as well as to the window**, and that matters here: the
 * sangeet and the reception both run to 3am, so the padded window spills past
 * midnight. Without the `dayKey` test those small hours would print twice —
 * once at the end of the 7th and again at the head of the 8th — and a strip
 * that runs 16:00 … 04:00 under a heading reading "Saturday" is wrong about
 * which day it is describing.
 */
export function hoursForDay(
  hours: Hour[],
  events: { start: string; end?: string }[],
  key: string,
): Hour[] {
  if (events.length === 0) return [];
  const HOUR = 60 * 60 * 1000;
  // **Rounded out to whole hours before the padding is added**, because the
  // forecast is only ever on the hour and the programme is not. The welcome
  // dinner starts at 17:30: measured from the half hour, "an hour before" is
  // 16:30, which excludes the 16:00 cell and leaves a 17:30 event with two
  // columns. Flooring the start and ceiling the end asks the question the
  // padding was meant to ask — the hour the thing begins in, and one before it.
  // Whole-hour arithmetic in UTC is safe here: Asia/Bangkok is a whole-hour
  // offset and has no DST, which is the assumption `lib/time.ts` already rests
  // on.
  const from = Math.floor(Math.min(...events.map((e) => Date.parse(e.start))) / HOUR) * HOUR - HOUR;
  const to = Math.ceil(Math.max(...events.map((e) => Date.parse(e.end ?? e.start))) / HOUR) * HOUR + HOUR;
  return hours.filter((h) => h.at >= from && h.at <= to && dayKey(h.at) === key);
}
